import {
  AppBar, Divider, Grid, Table, TableBody, TableCell, TableRow, Toolbar
} from '@material-ui/core';
import { ReactComponent as MLAlogo } from 'assets/images/logo-mla-white.svg';
import { ReactComponent as SGlogo } from 'assets/images/logo-sheep-genetics-white.svg';
import BreedingServiceLogo from 'components/breeding-service-logo';
import QRCode from 'qrcode.react';
import React from 'react';
import { Database } from 'shared/database.enum';
import './info-sheet.scss';

/**
 * Info sheet component, first page of pen cards pdf.
 */
export const InfoSheet: React.FC<{db: Database}> = (props) => {
  const {
    db = Database.MaternalAll,
  } = props;

  return (
    <div
      className="sg-info-sheet"
      id="info-sheet"
    >
      <AppBar
        className="sg-info-sheet__header"
        position="relative"
        elevation={0}
      >
        <Toolbar className="sg-header__container">
          <div className="sg-header__logo">
            <SGlogo />
            <MLAlogo />
          </div>
        </Toolbar>
      </AppBar>
      <Grid
        container
        item
        xs={12}
        className="sg-info-sheet__content"
      >
        <Grid
          container
          item
          xs={12}
          className="sg-info-sheet__title"
        >
          <h6>How to understand breeding values</h6>
          <Divider />
        </Grid>
        <Grid
          container
          item
          xs={12}
          spacing={3}
        >
          <Grid
            item
            xs={4}
          >
            <p className="text--xs text--bold">What is an ASBV?</p>
            <p className="text--xs">
              An ASBV is an estimate of the genetic potential a sheep will pass on to its progeny. ASBVs are available for a range of economically important traits and are designed to be used in conjunction with visual selection.
            </p>
            <p className="text--xs">
              The appearance and performance of an animal is a combination of its genes and the environment in which it is raised (e.g. the amount and quality of feed, single or twin birth type). ASBVs account for these environmental effects, allowing the comparison of sheep based on the genes they will pass on to their progeny.
            </p>
          </Grid>
          <Grid
            item
            xs={4}
          >
            <p className="text--xs text--bold">What is a selection index?</p>
            <p className="text--xs">
              To make selection easier, traits can be combined into a selection index. A selection index combines ASBVs for several traits to give a single value. This reflects the performance of the sheep relative to the breeding objective of the particular index.
            </p>
            <p className="text--xs">
              Index types include:
            </p>
            <p className="text--xs">
              $ Value Indexes: A dollar index indicates the value of an animal based on its suitability for a particular market.
            </p>
            <p className="text--xs">
              Desired Gains Indexes: Work on a proportional gain of a combination of traits.
            </p>
          </Grid>
          <Grid
            item
            xs={4}
          >
            <p className="text--xs text--bold">How to interpret ASBVs</p>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell><p className="text--sm text--bold">AWT</p></TableCell>
                  <TableCell><p className="text--xs">Trait acronym (age & trait combined e.g. Adult Weight)</p></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><h4>10.2</h4></TableCell>
                  <TableCell><p className="text--xs">ASBV expressed as +/- deviations from average</p></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><p className="text--xs">ACC. 81</p></TableCell>
                  <TableCell><p className="text--xs">Accuracy % based on amount of information available</p></TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><div className="sg-percentile-tag highlight--5">TOP 5%</div></TableCell>
                  <TableCell><p className="text--xs">Percentile highlight shows where an animal ranks for a trait within the analysis group</p></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Grid>
        </Grid>
        <Grid
          container
          item
          xs={12}
          className="sg-info-sheet__title"
        >
          <h6>What to look for on a pen card</h6><Divider />
        </Grid>
        <Grid
          container
          item
          xs={12}
          spacing={3}
        >
          <Grid
            container
            item
            xs={4}
            wrap="nowrap"
          >
            <Grid item>
              <div className="sg-info-sheet__qr"><QRCode
                size={108}
                value="https://search.sheepgenetics.org.au/"
              />
              </div>
            </Grid>
            <Grid item>
              <p className="text--xs text--bold">QR code</p>
              <p className="text--xxs">
                Scanning a QR code on a pen card with your phone camera will direct you to the animal’s url on the Sheep Genetics website with more ASBV data.
              </p>
            </Grid>
          </Grid>
          <Grid
            container
            item
            xs={8}
            wrap="nowrap"
          >
            <Grid item>
              <div className="sg-info-sheet__logo">
                <BreedingServiceLogo
                  className="selection-indexes__logo"
                  db={db}
                />
              </div>
            </Grid>
            <Grid
              container
              item
            >
              <Grid
                item
                xs={12}
              >
                <p className="text--xs text--bold">ASBV logos</p>
                <p className="text--xxs">
                  The four ASBV logos are your guarantee for across-flock breeding values describing an animal’s genetic merit.
                </p>
              </Grid>
              <Grid
                item
                xs={12}
              >
                <p className="text--xs text--bold">Analysis date</p>
                <p className="text--xxs">
                  The analysis date is located at the bottom left next to the ASBV logo. It indicates when the data was exported from Sheep Genetics database.
                </p>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};
